import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  CampaignCheck,
  FilterFieldOption,
} from '@internal/backstage-plugin-bidone-backend';
import {
  CheckInput,
  useAddCampaignCheck,
  useFilterOptions,
  useUpdateCampaignCheck,
} from '../../hooks/useCampaigns';

// json-rules-engine's built-in operators — campaign check rules are stored in
// the same shape as the rubric checks in app-config.yaml.
const RULE_OPERATORS = [
  { id: 'equal', label: '=' },
  { id: 'notEqual', label: '≠' },
  { id: 'greaterThan', label: '>' },
  { id: 'greaterThanInclusive', label: '≥' },
  { id: 'lessThan', label: '<' },
  { id: 'lessThanInclusive', label: '≤' },
  { id: 'in', label: 'in' },
  { id: 'notIn', label: 'not in' },
  { id: 'contains', label: 'contains' },
  { id: 'doesNotContain', label: "doesn't contain" },
] as const;

type RuleCondition = {
  factId: string;
  factKey: string;
  operator: string;
  value: string;
};

const factOptionId = (f: FilterFieldOption) => `${f.factId}.${f.factKey}`;

const coerceRuleValue = (
  raw: string,
  valueType: FilterFieldOption['valueType'] | undefined,
): string | number | boolean => {
  if (valueType === 'number') {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n;
  }
  if (valueType === 'boolean') return raw === 'true';
  return raw;
};

// Best-effort parse of a stored ruleJson back into builder rows. Conditions
// that don't fit the simple flat shape are dropped (the user re-adds them).
const parseRule = (
  ruleJson: string,
  factIds: string[],
): { combine: 'all' | 'any'; conditions: RuleCondition[] } => {
  const fallback = { combine: 'all' as const, conditions: [] };
  try {
    const parsed = JSON.parse(ruleJson);
    const root = parsed?.conditions;
    const combine: 'all' | 'any' = Array.isArray(root?.any) ? 'any' : 'all';
    const list = (Array.isArray(root?.any) ? root.any : root?.all) ?? [];
    const conditions: RuleCondition[] = [];
    for (const condition of list) {
      if (typeof condition?.fact === 'string' && condition?.operator) {
        conditions.push({
          factId: factIds[0] ?? '',
          factKey: condition.fact,
          operator: String(condition.operator),
          value:
            condition.value === undefined ? '' : String(condition.value),
        });
      }
    }
    return { combine, conditions };
  } catch {
    return fallback;
  }
};

const buildRuleJson = (
  combine: 'all' | 'any',
  conditions: RuleCondition[],
  factFields: FilterFieldOption[],
): string => {
  const ruleConditions = conditions.map(condition => {
    const field = factFields.find(
      f => f.factId === condition.factId && f.factKey === condition.factKey,
    );
    return {
      fact: condition.factKey,
      operator: condition.operator,
      value: coerceRuleValue(condition.value, field?.valueType),
    };
  });
  return JSON.stringify({ conditions: { [combine]: ruleConditions } });
};

type CheckEditorDialogProps = {
  open: boolean;
  // When omitted, onAddLocal must be provided (local staging mode).
  campaignId?: string;
  // When set the dialog edits this check; otherwise it creates a new one.
  check?: CampaignCheck | null;
  onClose: () => void;
  // Staging mode: called instead of the API when campaignId is absent.
  onAddLocal?: (input: CheckInput) => void;
};

export const CheckEditorDialog = ({
  open,
  campaignId,
  check,
  onClose,
  onAddLocal,
}: CheckEditorDialogProps) => {
  const { data: options } = useFilterOptions();
  const addCheck = useAddCampaignCheck();
  const updateCheck = useUpdateCampaignCheck();
  const localMode = !campaignId && !!onAddLocal;
  const saving = !localMode && (addCheck.isPending || updateCheck.isPending);
  const saveError = localMode ? null : (addCheck.error ?? updateCheck.error);

  const factFields = useMemo(
    () => (options?.fields ?? []).filter(f => f.kind === 'fact'),
    [options],
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [combine, setCombine] = useState<'all' | 'any'>('all');
  const [conditions, setConditions] = useState<RuleCondition[]>([]);
  const [kind, setKind] = useState<'fact' | 'manual'>('fact');

  useEffect(() => {
    if (open) {
      setName(check?.name ?? '');
      setDescription(check?.description ?? '');
      setKind(check?.kind ?? 'fact');
      const parsed = parseRule(check?.ruleJson ?? '', check?.factIds ?? []);
      setCombine(parsed.combine);
      setConditions(parsed.conditions);
      addCheck.reset();
      updateCheck.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, check]);

  const saveLabel = check ? 'Save check' : 'Add check';

  const handleSave = async () => {
    const factIds = Array.from(
      new Set(conditions.map(c => c.factId).filter(Boolean)),
    );
    const input: CheckInput = {
      name: name.trim(),
      description: description.trim(),
      kind,
      origin: check?.origin ?? 'campaign-defined',
      sourceRubricCheckId: check?.sourceRubricCheckId ?? null,
      factIds: kind === 'manual' ? [] : factIds,
      ruleJson:
        kind === 'manual'
          ? ''
          : buildRuleJson(combine, conditions, factFields),
      filterId: check?.filterId ?? null,
    };
    if (localMode) {
      onAddLocal!(input);
      onClose();
      return;
    }
    if (check) {
      await updateCheck.mutateAsync({
        campaignId: campaignId!,
        checkId: check.id,
        patch: input,
      });
    } else {
      await addCheck.mutateAsync({ campaignId: campaignId!, input });
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{check ? 'Edit check' : 'New check'}</DialogTitle>
      <DialogContent>
        {saveError ? (
          <Alert severity="error">{(saveError as Error).message}</Alert>
        ) : null}
        <TextField
          sx={{ mb: 2 }}
          label="Check name"
          variant="outlined"
          size="small"
          fullWidth
          value={name}
          onChange={e => setName(e.target.value)}
        />
        <TextField
          sx={{ mb: 2 }}
          label="Description"
          variant="outlined"
          size="small"
          fullWidth
          multiline
          minRows={2}
          value={description}
          onChange={e => setDescription(e.target.value)}
        />

        <TextField
          sx={{ mb: 2 }}
          label="Check type"
          variant="outlined"
          size="small"
          select
          fullWidth
          value={kind}
          onChange={e => setKind(e.target.value as 'fact' | 'manual')}
          helperText={
            kind === 'manual'
              ? 'Manual — a component owner marks this done from the matrix or their component page. No automated rule.'
              : 'Automated — evaluated from tech-insights facts.'
          }
        >
          <MenuItem value="fact">Automated (fact rule)</MenuItem>
          <MenuItem value="manual">Manual (owner attests)</MenuItem>
        </TextField>

        {kind === 'fact' && (
          <>
        <Typography variant="subtitle2" gutterBottom>
          Pass condition
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            my: 1,
          }}
        >
          <Typography variant="body2">Component passes when</Typography>
          <TextField
            sx={{ width: 90 }}
            select
            size="small"
            variant="outlined"
            value={combine}
            onChange={e => setCombine(e.target.value as 'all' | 'any')}
          >
            <MenuItem value="all">all</MenuItem>
            <MenuItem value="any">any</MenuItem>
          </TextField>
          <Typography variant="body2">of these hold:</Typography>
        </Box>

        {conditions.map((condition, index) => {
          const field =
            factFields.find(
              f =>
                f.factId === condition.factId &&
                f.factKey === condition.factKey,
            ) ?? null;
          const suggestions = (field?.values ?? []).map(String);
          const updateCondition = (next: Partial<RuleCondition>) =>
            setConditions(existing =>
              existing.map((c, i) => (i === index ? { ...c, ...next } : c)),
            );
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                mb: 1,
              }}
            >
              <Autocomplete
                sx={{ flex: 3, minWidth: 220 }}
                size="small"
                options={factFields}
                groupBy={f => f.group}
                getOptionLabel={f => f.label}
                isOptionEqualToValue={(a, b) =>
                  factOptionId(a) === factOptionId(b)
                }
                value={field}
                onChange={(_e, next) => {
                  // Ignore clear events — a condition always points at a fact.
                  if (next) {
                    updateCondition({
                      factId: next.factId!,
                      factKey: next.factKey!,
                      value: '',
                    });
                  }
                }}
                renderInput={params => (
                  <TextField {...params} label="Fact" variant="outlined" />
                )}
              />
              <TextField
                sx={{ flex: 2, minWidth: 160 }}
                label="Operator"
                variant="outlined"
                size="small"
                select
                value={condition.operator}
                onChange={e => updateCondition({ operator: e.target.value })}
              >
                {RULE_OPERATORS.map(op => (
                  <MenuItem key={op.id} value={op.id}>
                    {op.label}
                  </MenuItem>
                ))}
              </TextField>
              <Autocomplete
                sx={{ flex: 2, minWidth: 160 }}
                size="small"
                freeSolo
                options={suggestions}
                value={condition.value}
                onInputChange={(_e, next) => updateCondition({ value: next })}
                renderInput={params => (
                  <TextField {...params} label="Value" variant="outlined" />
                )}
              />
              <IconButton
                size="small"
                onClick={() =>
                  setConditions(existing =>
                    existing.filter((_c, i) => i !== index),
                  )
                }
                aria-label="Remove condition"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        })}
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={factFields.length === 0}
          onClick={() => {
            const first = factFields[0];
            setConditions(existing => [
              ...existing,
              {
                factId: first.factId!,
                factKey: first.factKey!,
                operator: 'equal',
                value: '',
              },
            ]);
          }}
        >
          Add condition
        </Button>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={
            !name.trim() ||
            (kind === 'fact' && conditions.length === 0) ||
            saving
          }
          onClick={() => {
            handleSave().catch(() => undefined);
          }}
        >
          {saving ? 'Saving…' : saveLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
